import React, {useRef, useMemo, useCallback, useState, useEffect} from 'react';
import moment from 'moment';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import {useTranslation} from 'react-i18next';
import {Event, getReservationInfo, isReservationAnnouncement} from '../../../../../utils';
import AppTooltip from '../../../../../components/common/AppTooltip';
import {useNavigate} from 'react-router';
import useLongPress from '../../../../../hooks/useLongPress';

const START_HOUR = 0;
const END_HOUR = 23;
// To show 3 events + 1 (+N) box, we need 4 total columns
const MAX_VISIBLE_COLUMNS = 4;

function DailyCalendar() {
  const {i18n} = useTranslation();
  const isRTL = i18n.language === 'heb';
  const {
    calendarData,
    calendarSelectedDate,
    setSelectedAnnouncementId,
    setShowAnnouncementViewModal,
    setCalendarDisplayType,
    selectedCalendarEvent,
    setSelectedCalendarEvent,
    openAddEditAnnouncementDialog,
    setOpenAddEditAnnouncementDialog,
  } = useOperationsContext();
  const navigate = useNavigate();
  const [selectedCalendarSlotStart, setSelectedCalendarSlotStart] = useState<string | null>(null);

  const isToday =
    moment().format('YYYY/MM/DD') ===
    moment(calendarSelectedDate).format('YYYY/MM/DD');
  const nowHHmm = moment().format('HH:mm');
  const timeSlots = Array.from(
    {length: END_HOUR - START_HOUR + 1},
    (_, i) => START_HOUR + i,
  );

  const getOffset = (time: string) => {
    const totalMinutes = (END_HOUR - START_HOUR + 1) * 60;
    const momentTime = moment(time, 'HH:mm');
    const minutesSinceStart =
      (momentTime.hours() - START_HOUR) * 60 + momentTime.minutes();
    return (minutesSinceStart / totalMinutes) * 100;
  };

  const isNowWithinRange = () => {
    const m = moment(nowHHmm, 'HH:mm');
    const mins = m.hours() * 60 + m.minutes();
    const startMins = START_HOUR * 60;
    const endMins = END_HOUR * 60;
    return mins >= startMins && mins <= endMins;
  };

  const hasCurrentEvent =
    isToday &&
    Array.isArray(calendarData?.single) &&
    calendarData.single.some((e: any) => {
      if (!e?.start || !e?.end) return false;
      return e.start <= nowHHmm && e.end >= nowHHmm;
    });

  const showNowLine = isToday && isNowWithinRange() && !hasCurrentEvent;

  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const longPressEventRef = useRef<any>(null);
  const didLongPressRef = useRef(false);

  useEffect(() => {
    if (!openAddEditAnnouncementDialog) {
      setSelectedCalendarSlotStart(null);
    }
  }, [openAddEditAnnouncementDialog]);

  const clearSelectedCalendarSelection = useCallback(() => {
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    setSelectedCalendarEvent(null);
  }, [setSelectedCalendarEvent]);

  useEffect(() => {
    if (!selectedCalendarEvent) return;

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-calendar-event-box="true"]')) return;
      if (target.closest('[data-calendar-selection-keep="true"]')) return;
      if (target.closest('.MuiMenu-root, .MuiPopover-root')) return;
      if (target.closest('.fixed.z-50')) return;

      clearSelectedCalendarSelection();
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true);
    };
  }, [clearSelectedCalendarSelection, selectedCalendarEvent]);

  const handleSingleClick = useCallback(
    (event: any) => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      setSelectedCalendarEvent(event);
      clickTimeoutRef.current = setTimeout(() => {
        setSelectedAnnouncementId(event?.id);
        setShowAnnouncementViewModal(true);
      }, 250);
    },
    [
      setSelectedAnnouncementId,
      setSelectedCalendarEvent,
      setShowAnnouncementViewModal,
    ],
  );

  const handleLongPress = useCallback(
    () => {
      if (!longPressEventRef.current) return;
      didLongPressRef.current = true;
      setSelectedCalendarEvent(longPressEventRef.current);
    },
    [setSelectedCalendarEvent],
  );
  const longPressHandlers = useLongPress<HTMLElement>(handleLongPress);

  const getLongPressProps = useCallback(
    (event: any) => ({
      ...longPressHandlers,
      onPointerDown: (pointerEvent: React.PointerEvent<HTMLElement>) => {
        didLongPressRef.current = false;
        longPressEventRef.current = event;
        longPressHandlers.onPointerDown(pointerEvent);
      },
    }),
    [longPressHandlers],
  );

  const openAddEventAtTime = useCallback(
    (startTime: string) => {
      setSelectedCalendarSlotStart(startTime);
      const endTime = moment(startTime, 'HH:mm').add(1, 'hour').format('HH:mm');
      navigate('/operations', {
        state: {
          showInCalendar: true,
          calendarStartTime: startTime,
          calendarEndTime: endTime,
        },
      });
      setOpenAddEditAnnouncementDialog(true);
    },
    [navigate, setOpenAddEditAnnouncementDialog],
  );

  const handleHourClick = useCallback(
    (hour: number) => {
      openAddEventAtTime(moment({hour}).format('HH:mm'));
    },
    [openAddEventAtTime],
  );

  const handleGridClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const clickY = Math.min(
        Math.max(event.clientY - rect.top, 0),
        rect.height,
      );
      const totalMinutes = (END_HOUR - START_HOUR + 1) * 60;
      const roundedMinutes =
        Math.floor(((clickY / rect.height) * totalMinutes) / 60) * 60;
      const startTime = moment()
        .startOf('day')
        .add(START_HOUR * 60 + roundedMinutes, 'minutes')
        .format('HH:mm');

      openAddEventAtTime(startTime);
    },
    [openAddEventAtTime],
  );

  const positionedEvents = useMemo(() => {
    if (!Array.isArray(calendarData?.single)) return [];

    const events = calendarData.single
      .filter((e: any) => e.start && e.end && !e.isFullDay)
      .sort((a: any, b: any) => a.start.localeCompare(b.start));

    const eventGroups: any[][] = [];

    // 1. Group events that collide temporally
    events.forEach((event: any) => {
      let placedInGroup = false;
      for (const group of eventGroups) {
        const latestEnd = Math.max(
          ...group.map(e => parseInt(e.end.replace(':', ''), 10)),
        );
        const currentStart = parseInt(event.start.replace(':', ''), 10);
        if (currentStart < latestEnd) {
          group.push(event);
          placedInGroup = true;
          break;
        }
      }
      if (!placedInGroup) eventGroups.push([event]);
    });

    // 2. Calculate columns and handle +N logic
    return eventGroups.flatMap(group => {
      const columns: any[][] = [];
      group.forEach(event => {
        let placedInColumn = false;
        for (let i = 0; i < columns.length; i++) {
          if (event.start >= columns[i][columns[i].length - 1].end) {
            columns[i].push(event);
            placedInColumn = true;
            break;
          }
        }
        if (!placedInColumn) columns.push([event]);
      });

      const totalCols = columns.length;
      // We show the "+N" if there are more than 3 columns total
      const showPlusMore = totalCols > 3;

      // If showing "+N", the grid is always 4 columns wide
      const effectiveCols = showPlusMore ? MAX_VISIBLE_COLUMNS : totalCols;
      const colWidth = 100 / effectiveCols;

      const renderedItems: any[] = [];

      columns.forEach((column, colIdx) => {
        // If we have 4 or more columns, the 4th slot (index 3) becomes the "+N" indicator
        if (showPlusMore && colIdx >= 3) {
          if (colIdx === 3) {
            renderedItems.push({
              isMoreIndicator: true,
              count: totalCols - 3, // Count everything from the 4th column onwards
              start: column[0].start,
              end: column[0].end,
              calculatedWidth: `${colWidth}%`,
              calculatedPosition: `${colIdx * colWidth}%`,
            });
          }
          return;
        }

        column.forEach(event => {
          renderedItems.push({
            ...event,
            calculatedWidth: `${colWidth}%`,
            calculatedPosition: `${colIdx * colWidth}%`,
            // Overlapping logic: row if single, col if multiple
            isOverlapping: totalCols > 1,
          });
        });
      });

      return renderedItems;
    });
  }, [calendarData?.single]);

  return (
    <>
      <div className="px-4 overflow-y-auto max-h-[200px] py-2">
        {calendarData?.allDay?.map((event: Event) => (
          <button
            key={event.id}
            data-calendar-event-box="true"
            onClick={e => {
              e.stopPropagation();
              if (didLongPressRef.current) return;
              handleSingleClick(event);
            }}
            {...getLongPressProps(event)}
            className={`w-full text-left bg-paleAqua rounded-md mb-1 hover:bg-icyBlue py-2 ${
              selectedCalendarEvent?.id === event.id
                ? 'border-2 border-linkBlue'
                : 'border border-icyBlue'
            }`}>
            <p className="text-sm text-black font-semibold text-center truncate px-2">
              {event.title}
            </p>
          </button>
        ))}
      </div>

      <div className="relative flex-1 min-h-0 overflow-y-auto p-4">
        <div className="relative" style={{height: '800px'}}>
          {timeSlots.map((hour, idx) => (
            <div
              key={idx}
              className="absolute right-0 left-0 flex items-start gap-2"
              style={{
                top: `${((hour - START_HOUR) / (END_HOUR - START_HOUR + 1)) * 100}%`,
              }}>
              <button
                type="button"
                className="text-sm text-gray-500 w-10 text-start"
                style={{transform: 'translateY(-50%)'}}
                onClick={() => handleHourClick(hour)}>
                {moment({hour}).format('HH:mm')}
              </button>
              <div className="flex-1 h-px bg-lightDarkGray" />
            </div>
          ))}

          {showNowLine && (
            <div
              className={`absolute left-0 ${
                i18n.language === 'heb'
                  ? 'right-[45px] left-0'
                  : 'right-0 left-[45px]'
              } pointer-events-none`}
              style={{top: `${getOffset(nowHHmm)}%`}}>
              <div className="w-full h-[3px] bg-raspberryRed" />
            </div>
          )}

          <div
            className={`absolute inset-0 ${isRTL ? 'mr-12' : 'ml-12'}`}
            onClick={handleGridClick}>
            {selectedCalendarSlotStart && (
              <div
                className="absolute pointer-events-none rounded-md border-2 !border-[#007AFF]"
                style={{
                  top: `${getOffset(selectedCalendarSlotStart)}%`,
                  height: `${100 / (END_HOUR - START_HOUR + 1)}%`,
                  right: 0,
                  left: 0,
                }}
              />
            )}

            {positionedEvents.map((event: any, index: number) => {
              const top = getOffset(event.start);
              const height = getOffset(event.end) - top;
              const isCurrent =
                isToday && event.start <= nowHHmm && event.end >= nowHHmm;
              const isPast = isToday && event.end < nowHHmm;
              const isActiveStyle = isCurrent || isPast;

              if (event.isMoreIndicator) {
                return (
                  <div
                    key={`more-${index}`}
                    className="absolute p-[1px] cursor-pointer"
                    style={{
                      top: `${top}%`,
                      height: `${height}%`,
                      width: event.calculatedWidth,
                      [isRTL ? 'right' : 'left']: event.calculatedPosition,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      setCalendarDisplayType('scheduled');
                    }}>
                    <div
                      className={`flex items-center justify-center h-full rounded-md border transition-colors ${isActiveStyle ? 'bg-[#EBEBEB] border-lightDarkGray text-grayText' : 'bg-paleAqua border-icyBlue text-black'}`}>
                      <p className="text-xs font-bold text-black">
                        {event.count}+
                      </p>
                    </div>
                  </div>
                );
              }

              const isSelected = selectedCalendarEvent?.id === event.id;
              const isReservation = isReservationAnnouncement(event?.source);
              const { zoneColor, label: reservationLabel } = isReservation
                ? getReservationInfo(event.source)
                : { zoneColor: undefined, label: "" };

              return (
                <div
                  key={event.id ?? index}
                  className="absolute p-[1px] transition-all duration-150"
                  style={{
                    top: `${top}%`,
                    height: `${height}%`,
                    width: event.calculatedWidth,
                    [isRTL ? "right" : "left"]: event.calculatedPosition,
                  }}
                >
                  <AppTooltip
                    title={`${event.start} - ${event.end} ${event.title}`}
                    backgroundColor="#E9FAFF"
                  >
                    <div
                      data-calendar-event-box="true"
                      className={`flex h-full rounded-md px-1 py-1 cursor-pointer hover:shadow-md
                      ${event.isOverlapping ? "flex-col justify-center items-center !py-0" : "flex-row justify-center items-center gap-2"}
                      ${isSelected ? "border-2 !border-[#007AFF]" : "border"}
                      ${!isSelected && (isActiveStyle ? "bg-[#EBEBEB] border-lightDarkGray text-grayText" : "bg-paleAqua border-icyBlue text-black")}
                      ${isSelected && (isActiveStyle ? "bg-[#EBEBEB] text-grayText" : "bg-paleAqua text-black")}`}
                      style={
                        isReservation && zoneColor && !isPast && !isCurrent
                          ? {
                              backgroundColor: zoneColor,
                              borderColor: zoneColor,
                            }
                          : undefined
                      }
                      onClick={e => {
                        e.stopPropagation();
                        if (didLongPressRef.current) return;
                        handleSingleClick(event);
                      }}
                      {...getLongPressProps(event)}
                    >
                      <p
                        className={`whitespace-nowrap ${event.isOverlapping ? "text-[10px] leading-tight" : "text-xs font-bold"}`}
                      >
                        {event.start}
                        {event.end ? ` - ${event.end}` : ""}
                      </p>
                      <p
                        className={`truncate text-center text-xs font-semibold ${event.isOverlapping ? "leading-none w-full" : ""}`}
                      >
                        {isReservation ? reservationLabel : event.title}
                      </p>
                    </div>
                  </AppTooltip>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default DailyCalendar;
